/**
 * Scraper Configuration Schema Validator
 * Validates scraper config data structure with security constraints
 */
import { z } from "zod";
// ============================================
// TRANSFORM FUNCTION WHITELIST
// ============================================
const transformFunctionSchema = z.enum([
    "trim",
    "lowercase",
    "uppercase",
    "extractNumber",
    "extractDate",
    "removeSpaces",
]);
// ============================================
// FIELD EXTRACTOR SCHEMA
// ============================================
const fieldExtractorSchema = z.object({
    selector: z.string().min(1, "Selector cannot be empty"),
    attribute: z.string().optional(),
    regex: z.string().optional(),
    transform: transformFunctionSchema.optional(),
    required: z.boolean().optional(),
});
// ============================================
// SELECTORS SCHEMA
// ============================================
const scraperSelectorsSchema = z.object({
    vehicleCard: z.string().min(1, "vehicleCard selector is required"),
    title: fieldExtractorSchema,
    variant: fieldExtractorSchema,
    price: fieldExtractorSchema,
    mileage: fieldExtractorSchema,
    fuel: fieldExtractorSchema,
    power: fieldExtractorSchema.optional(),
    registrationDate: fieldExtractorSchema,
    location: fieldExtractorSchema,
    dealerName: fieldExtractorSchema,
    imageUrl: fieldExtractorSchema,
    detailUrl: fieldExtractorSchema,
    dealerLocation: fieldExtractorSchema.optional(),
    priceRating: fieldExtractorSchema.optional(),
    details: fieldExtractorSchema.optional(),
    sponsoredBadge: fieldExtractorSchema.optional(),
    accidentDamage: fieldExtractorSchema.optional(),
    totalVehiclesCount: fieldExtractorSchema.optional(),
});
// ============================================
// CUSTOM FILTER SCHEMA
// ============================================
const filterOperatorSchema = z.enum([
    "equals",
    "contains",
    "gt",
    "lt",
    "gte",
    "lte",
    "regex",
]);
const customFilterSchema = z.object({
    field: z.string().min(1, "Field name is required"),
    operator: filterOperatorSchema,
    value: z.union([z.string(), z.number()]),
    exclude: z.boolean().optional(),
});
// ============================================
// FILTERS SCHEMA
// ============================================
const scraperFiltersSchema = z.object({
    excludeSponsored: z.boolean().optional(),
    excludeAccidentDamage: z.boolean().optional(),
    customFilters: z.array(customFilterSchema).optional(),
});
// ============================================
// IMAGE DOWNLOAD SCHEMA
// ============================================
const imageDownloadConfigSchema = z.object({
    enabled: z.boolean(),
    maxImagesPerVehicle: z.number().int().min(1).max(10).optional(),
    targetFolder: z.string().optional(),
    timeout: z.number().int().min(1000).max(30000).optional(),
});
// ============================================
// PAGINATION SCHEMA (PREPARED FOR FUTURE)
// ============================================
const paginationConfigSchema = z.object({
    nextButton: z.string().optional(),
    maxPages: z.number().int().min(1).max(100).optional(),
});
// ============================================
// COMPLETE CONFIG DATA SCHEMA
// ============================================
export const scraperConfigDataSchema = z.object({
    selectors: scraperSelectorsSchema,
    filters: scraperFiltersSchema.optional(),
    imageDownload: imageDownloadConfigSchema.optional(),
    pagination: paginationConfigSchema.optional(),
});
// ============================================
// CREATE SCRAPER CONFIG REQUEST SCHEMA
// ============================================
export const createScraperConfigSchema = z.object({
    name: z
        .string()
        .min(3, "Name must be at least 3 characters")
        .max(100, "Name must not exceed 100 characters"),
    target_url_pattern: z
        .string()
        .min(1, "Target URL pattern is required")
        .refine((pattern) => {
        try {
            new RegExp(pattern);
            return true;
        }
        catch {
            return false;
        }
    }, "Invalid regex pattern"),
    config_data: scraperConfigDataSchema,
    change_reason: z.string().max(500).optional(),
});
// ============================================
// UPDATE SCRAPER CONFIG REQUEST SCHEMA
// ============================================
export const updateScraperConfigSchema = z.object({
    name: z
        .string()
        .min(3, "Name must be at least 3 characters")
        .max(100, "Name must not exceed 100 characters")
        .optional(),
    target_url_pattern: z
        .string()
        .min(1, "Target URL pattern is required")
        .refine((pattern) => {
        try {
            new RegExp(pattern);
            return true;
        }
        catch {
            return false;
        }
    }, "Invalid regex pattern")
        .optional(),
    config_data: scraperConfigDataSchema.optional(),
    change_reason: z.string().max(500).optional(),
});
// ============================================
// ACTIVATE SCRAPER CONFIG REQUEST SCHEMA
// ============================================
export const activateScraperConfigSchema = z.object({
    change_reason: z.string().max(500).optional(),
});
// ============================================
// TEST SCRAPER CONFIG REQUEST SCHEMA
// ============================================
export const testScraperConfigSchema = z.object({
    test_url: z.string().url("Invalid test URL"),
    config_data: scraperConfigDataSchema,
});
//# sourceMappingURL=scraperConfig.schema.js.map